import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  MessageSquare, ClipboardList, FileText, ClipboardCheck,
  ReceiptIndianRupee, Wallet, X, ArrowRight, ArrowLeft, Sparkles,
} from "lucide-react";

export const openDemoTour = () => window.dispatchEvent(new Event("open-demo-tour"));

const STEPS = [
  { icon: MessageSquare, tone: "#25D366", route: "/inbox", tag: "WhatsApp Inbox",
    title: "1 · It starts on WhatsApp",
    body: "A customer messages your shop. Every chat lands in one shared WhatsApp Inbox — no more requirements lost across staff phones." },
  { icon: ClipboardList, tone: "#1D4ED8", route: "/enquiries", tag: "Enquiries",
    title: "2 · Turn the chat into an Enquiry",
    body: "Capture the requirement as a tracked enquiry with an owner, priority and a two-clock SLA so nothing slips." },
  { icon: FileText, tone: "#F83A84", route: "/sales?tab=quotes", tag: "Quotes",
    title: "3 · Send a GST Quote",
    body: "Build a priced, GST-correct quotation in seconds and send the PDF straight back to the customer on WhatsApp." },
  { icon: ClipboardCheck, tone: "#7C3AED", route: "/sales?tab=orders", tag: "Sales Orders",
    title: "4 · Convert to a Sales Order",
    body: "When the quote is accepted, one tap turns it into a confirmed order and reserves or deducts stock from your warehouse." },
  { icon: ReceiptIndianRupee, tone: "#0B7C5A", route: "/sales?tab=invoices", tag: "Invoices",
    title: "5 · Raise a GST Invoice",
    body: "Convert the order into a compliant tax invoice with the right CGST/SGST or IGST, ready to share and print." },
  { icon: Wallet, tone: "#B45309", route: "/sales?tab=invoices", tag: "Payments",
    title: "6 · Record the Payment",
    body: "Log full or part payments against the invoice. Receivables and overdue amounts update live on your dashboard." },
];

export function DemoTour() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const key = tenant?.id ? `tradedesk.demoTourSeen.${tenant.id}` : null;

  useEffect(() => {
    if (tenant?.is_demo && key && !localStorage.getItem(key)) {
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [tenant, key]);

  useEffect(() => {
    const h = () => { setI(0); setOpen(true); };
    window.addEventListener("open-demo-tour", h);
    return () => window.removeEventListener("open-demo-tour", h);
  }, []);

  if (!tenant?.is_demo || !open) return null;

  const close = () => { if (key) localStorage.setItem(key, "1"); setOpen(false); };
  const finish = () => { close(); navigate("/inbox"); };
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[110] bg-[#101D51]/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demo-tour">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-up">
        {/* header */}
        <div className="relative px-6 pt-6 pb-5 bg-[#101D51] text-white">
          <button onClick={close} data-testid="demo-tour-close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/60">
            <Sparkles className="w-3.5 h-3.5 text-[#F83A84]" /> Welcome to the TradeDesk360 demo
          </div>
          <p className="mt-2 text-sm text-white/70 max-w-sm">
            Here's the full journey — a WhatsApp message becomes a paid invoice. Everything you see is sample data you can play with freely.
          </p>
        </div>

        {/* body */}
        <div className="px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl shrink-0 flex items-center justify-center"
              style={{ backgroundColor: `${step.tone}1A` }}>
              <Icon className="w-6 h-6" style={{ color: step.tone }} />
            </div>
            <div className="min-w-0">
              <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1.5"
                style={{ backgroundColor: `${step.tone}1A`, color: step.tone }}>{step.tag}</span>
              <h3 className="ff-head text-lg font-bold text-[#101D51]">{step.title}</h3>
              <p className="text-sm text-[#6B7189] mt-1.5 leading-relaxed">{step.body}</p>
            </div>
          </div>

          {/* progress dots */}
          <div className="flex items-center gap-1.5 mt-6" data-testid="demo-tour-dots">
            {STEPS.map((s, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Step ${idx + 1}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{ width: idx === i ? 28 : 8, backgroundColor: idx === i ? step.tone : "#E6E8F0" }} />
            ))}
          </div>

          {/* actions */}
          <div className="flex items-center justify-between mt-6">
            <button onClick={close} data-testid="demo-tour-skip"
              className="text-sm text-[#6B7189] hover:text-[#101D51] transition-colors">Skip tour</button>
            <div className="flex items-center gap-2">
              {i > 0 && (
                <button onClick={() => setI(i - 1)} data-testid="demo-tour-back"
                  className="h-10 px-4 rounded-lg border border-[#E6E8F0] text-sm font-medium text-[#101D51] hover:bg-[#F5F6FA] flex items-center gap-1.5 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              {!last ? (
                <button onClick={() => setI(i + 1)} data-testid="demo-tour-next"
                  className="h-10 px-5 rounded-lg bg-[#F83A84] text-white text-sm font-semibold hover:bg-[#E02871] flex items-center gap-1.5 transition-colors">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={finish} data-testid="demo-tour-finish"
                  className="h-10 px-5 rounded-lg bg-[#0B7C5A] text-white text-sm font-semibold hover:bg-[#0a6b4e] flex items-center gap-1.5 transition-colors">
                  Start exploring <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
